import { Col, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { AmountUnit } from '../../../../../api/types';
import RadioGroupField from '../../../../../components/fields/RadioGroupField';
import FormFields from '../../../../../themes/admin/Fields/FormFields';
import { CommonType } from '../../shared/typings';
import useFinanceProductOptions from '../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../index';
import DefaultIntervalField from './DefaultIntervalField';
import YesNoField from './YesNoField';

const colSpan = { lg: 8, xs: 24 };

const SecurityDeposit = ({ disabled = false }: { disabled?: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);

    const { commonTypeOptions } = useFinanceProductOptions();

    const { values } = useFormikContext<FinanceProductFormValues>();
    const { deposit } = values;
    const { min = 0, max = 0 } = deposit;

    return (
        <Row gutter={[16, 16]}>
            <Col span={24}>
                <RadioGroupField
                    {...t('financeProductDetails:fields.depositType', { returnObjects: true })}
                    disabled={disabled}
                    name="deposit.type"
                    options={commonTypeOptions}
                    required
                />
            </Col>
            {deposit?.type === CommonType.Range && (
                <>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.depositMin', { returnObjects: true })}
                            disabled={disabled}
                            max={deposit?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="deposit.min"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.depositMax', { returnObjects: true })}
                            disabled={disabled}
                            max={deposit?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="deposit.max"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <FormFields.InputNumberField
                            {...t('financeProductDetails:fields.depositStep', { returnObjects: true })}
                            disabled={min >= max || disabled}
                            max={deposit?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="deposit.step"
                            required
                        />
                    </Col>
                    <Col {...colSpan}>
                        <DefaultIntervalField
                            {...t('financeProductDetails:fields.depositDefault', { returnObjects: true })}
                            disabled={disabled}
                            max={deposit?.unit === AmountUnit.Percentage ? 100 : Number.MAX_SAFE_INTEGER}
                            min={0}
                            name="deposit.default"
                            unitProps={{ name: 'deposit.unit' }}
                            required
                        />
                    </Col>
                </>
            )}
            <Col {...colSpan}>
                <YesNoField
                    {...t('financeProductDetails:fields.depositViewable', { returnObjects: true })}
                    disabled={disabled}
                    name="deposit.isViewable"
                />
            </Col>
            {deposit?.type === CommonType.Range && (
                <Col {...colSpan}>
                    <YesNoField
                        {...t('financeProductDetails:fields.depositEditable', { returnObjects: true })}
                        disabled={!deposit?.isViewable || disabled}
                        name="deposit.isEditable"
                    />
                </Col>
            )}
        </Row>
    );
};

export default SecurityDeposit;
